/* Mutation Breeding Guide modal — M1-M2-M3 scheme, physical vs chemical
   mutagens, ICAR guidelines, standard doses for Indian crops. */
import React from 'react';
import { X, BookOpen, Zap, FlaskConical, Shield } from 'lucide-react';

export default function MutationBreedingGuide({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="mutation-guide-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-amber-200"
          style={{ background: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)' }}>
          <div className="flex items-center gap-2">
            <BookOpen size={20} style={{ color: '#B45309' }} />
            <h2 className="font-display text-xl" style={{ color: '#78350F' }}>Mutation Breeding Guide</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-amber-100" data-testid="close-guide">
            <X size={18} style={{ color: '#B45309' }} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* M1-M2-M3 scheme */}
          <section>
            <h3 className="font-display mb-2" style={{ fontSize: 16, color: '#78350F' }}>M1 → M2 → M3 Generation Scheme</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { g: 'M1', title: 'Treatment generation', txt: 'Seeds treated with mutagen → grow M1 plants. Chimeric → do NOT select here. Bulk harvest all fertile spikes/pods for M2 seed.' },
                { g: 'M2', title: 'Segregation generation', txt: 'Sow M2 seed in single-seed-descent rows. Score chlorophyll mutants in seedling stage; score morphological & quantitative mutants throughout. This is the main screening generation.' },
                { g: 'M3', title: 'Confirmation generation', txt: 'Advance selected M2 plants as M3 progeny rows. Use chi-square segregation tests (3:1 / 15:1) to confirm inheritance. True-breeding M3 families → advance to preliminary yield trials.' },
              ].map(s => (
                <div key={s.g} className="rounded-xl p-4 border-l-4" style={{ borderLeftColor: '#D97706', background: '#FFFBEB' }}>
                  <div className="font-bold text-lg" style={{ color: '#B45309' }}>{s.g}</div>
                  <div className="font-semibold text-sm mt-0.5">{s.title}</div>
                  <p className="text-xs mt-1 leading-relaxed">{s.txt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Physical vs Chemical mutagens */}
          <section>
            <h3 className="font-display mb-2 flex items-center gap-2" style={{ fontSize: 16, color: '#78350F' }}>
              <Zap size={16} /> Physical vs Chemical Mutagens
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead><tr className="bg-amber-100 text-amber-900">
                  {['Type', 'Examples', 'Target', 'Advantages', 'Limitations'].map(h => (
                    <th key={h} className="px-3 py-2 border border-amber-200 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  <tr className="bg-white">
                    <td className="px-3 py-2 border border-amber-200 font-semibold">Physical</td>
                    <td className="px-3 py-2 border border-amber-200">Gamma rays (⁶⁰Co), X-rays, neutrons, UV</td>
                    <td className="px-3 py-2 border border-amber-200">Whole genome (chromosomal breaks, deletions)</td>
                    <td className="px-3 py-2 border border-amber-200">Deep penetration, reproducible, safe for large seed lots</td>
                    <td className="px-3 py-2 border border-amber-200">Produces many large deletions and chromosomal aberrations</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-3 py-2 border border-amber-200 font-semibold">Chemical</td>
                    <td className="px-3 py-2 border border-amber-200">EMS, DES, MMS, sodium azide (NaN₃), NMU, NEU</td>
                    <td className="px-3 py-2 border border-amber-200">Point mutations (G:C → A:T transitions dominate)</td>
                    <td className="px-3 py-2 border border-amber-200">High frequency of point mutations, minimal chromosomal damage</td>
                    <td className="px-3 py-2 border border-amber-200">Hazardous; precise dose/duration control needed</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Standard doses for Indian crops */}
          <section>
            <h3 className="font-display mb-2 flex items-center gap-2" style={{ fontSize: 16, color: '#78350F' }}>
              <FlaskConical size={16} /> Standard LD50 / GR50 for Indian Crops
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-amber-100 text-amber-900">
                  {['Crop', 'Gamma (Gy)', 'EMS (%)', 'Treatment time', 'Notes'].map(h => (
                    <th key={h} className="px-2 py-1.5 border border-amber-200">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[
                    ['Wheat', '200–300', '0.2–0.4', '6–8 h', 'Pre-soak 6 h'],
                    ['Rice', '250–350', '0.2–0.3', '6 h', 'Dehull for EMS'],
                    ['Maize', '250–300', '0.3–0.4', '4 h', 'Dry seed for Gamma'],
                    ['Mustard', '500–800', '0.4–0.5', '4 h', 'Small seed — adjust time'],
                    ['Chickpea', '250–300', '0.3', '6 h', '-'],
                    ['Groundnut', '200–250', '0.2–0.3', '4–6 h', 'Kernels only'],
                    ['Cotton', '150–200', '0.2–0.3', '4 h', 'Delinted seed'],
                    ['Sugarcane', '30–50 (setts)', 'N/A', '-', 'Vegetative; low dose'],
                  ].map(row => (
                    <tr key={row[0]} className="bg-white hover:bg-amber-50">
                      {row.map((c, i) => <td key={i} className="px-2 py-1 border border-amber-200">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ICAR guidelines */}
          <section>
            <h3 className="font-display mb-2 flex items-center gap-2" style={{ fontSize: 16, color: '#78350F' }}>
              <Shield size={16} /> ICAR / BARC / NBPGR Guidelines
            </h3>
            <ul className="text-sm space-y-1 list-disc pl-5">
              <li>Pre-register mutant lines with <b>NBPGR</b> (ICAR-IC numbers) before release.</li>
              <li>Submit three-year DUS (Distinctness, Uniformity, Stability) trial data under the <b>PPV&amp;FR Act 2001</b>.</li>
              <li>Conduct ≥3 years of AICRP / state-level VCU (Value for Cultivation and Use) trials at ≥8 locations.</li>
              <li>Radiation facilities: <b>BARC Trombay</b>, <b>IARI (IANWGR)</b>, <b>TNAU gamma chamber</b>, <b>ANGRAU RARS Anakapalle</b>.</li>
              <li>Use FAO/IAEA Mutation Breeding Manual (2nd Ed.) SOPs for dosimetry and handling.</li>
              <li>Safety: EMS solutions must be neutralised with 1 M NaOH before disposal; dispose in dedicated chemical waste containers.</li>
            </ul>
          </section>

          {/* References */}
          <section className="rounded-xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
            <h4 className="font-semibold text-xs uppercase tracking-wider mb-1" style={{ color: '#92400E' }}>Key references</h4>
            <ul className="text-xs space-y-0.5" style={{ color: '#78350F' }}>
              <li>Gaul, H. (1964). <i>Mutations in Plant Breeding.</i> Radiation Botany.</li>
              <li>FAO/IAEA (1977). <i>Manual on Mutation Breeding</i>, 2nd Ed.</li>
              <li>Konzak, C.F. et al. (1965). Efficient chemical mutagenesis. IAEA Tech. Rep. 47.</li>
              <li>Kharkwal, M.C. (2012). <i>ICAR Handbook on Mutation Breeding in India.</i></li>
              <li>PPV&amp;FR Authority (2007). <i>Guidelines for the conduct of tests for DUS on new varieties.</i></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
